import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { TenantProvider } from '@context/TenantContext';
import { AuthProvider } from '@context/AuthContext';
import { CartProvider } from '@context/CartContext';
import { EventProvider } from '@context/EventContext';
import { FavoritesProvider } from '@context/FavoritesContext';
import { ROUTES } from '@utils/constants';

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

function RecaptchaWrapper({ children }: { children: React.ReactNode }) {
  if (!recaptchaSiteKey) {
    return <>{children}</>;
  }
  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      {children}
    </GoogleReCaptchaProvider>
  );
}

// Components
import { TenantLoadingScreen } from '@components/TenantLoadingScreen';

// Pages
import HomePage from '@pages/HomePage';
import EventsPage from '@pages/EventsPage';
import EventGalleryPage from '@pages/EventGalleryPage';
import CartPage from '@pages/CartPage';
import CheckoutPage from '@pages/CheckoutPage';
import LoginPage from '@pages/LoginPage';
import RegisterPage from '@pages/RegisterPage';
import AuthCallbackPage from '@pages/AuthCallbackPage';
import MagicLinkVerifyPage from '@pages/MagicLinkVerifyPage';
import EmailVerifyPage from '@pages/EmailVerifyPage';
import TermsOfServicePage from '@pages/TermsOfServicePage';
import PrivacyPolicyPage from '@pages/PrivacyPolicyPage';
import RefundPolicyPage from '@pages/RefundPolicyPage';
import PricingPage from '@pages/PricingPage';
import OrderHistoryPage from '@pages/OrderHistoryPage';

// Protected Route Component
import ProtectedRoute from '@components/auth/ProtectedRoute';

// Dashboard App
import DashboardApp from '@components/dashboard/DashboardApp';
import AdminLayout from '@pages/admin/AdminLayout';
import ShowcaseModeration from '@pages/admin/ShowcaseModeration';
import AdminDashboard from '@pages/admin/AdminDashboard';
import TenantsList from '@pages/admin/TenantsList';
import TenantDetails from '@pages/admin/TenantDetails';
import UsersPage from '@pages/admin/UsersPage';
import SystemLogsPage from '@pages/admin/SystemLogsPage';
import AuditLogsPage from '@pages/admin/AuditLogsPage';

function App() {
  return (
    <BrowserRouter>
      <RecaptchaWrapper>
      <TenantProvider>
        <TenantLoadingScreen />
        <AuthProvider>
          <CartProvider>
            <EventProvider>
              <FavoritesProvider>
                <Toaster position="top-right" richColors closeButton />
              <Routes>
              {/* Public Routes */}
              <Route path={ROUTES.HOME} element={<HomePage />} />
              <Route path={ROUTES.EVENTS} element={<EventsPage />} />
              <Route path={ROUTES.EVENT_GALLERY} element={<EventGalleryPage />} />
              <Route path={ROUTES.LOGIN} element={<LoginPage />} />
              <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/auth/magic-link/verify" element={<MagicLinkVerifyPage />} />
              <Route path={ROUTES.VERIFY_EMAIL} element={<EmailVerifyPage />} />

              {/* Legal Pages */}
              <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfServicePage />} />
              <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
              <Route path={ROUTES.REFUND_POLICY} element={<RefundPolicyPage />} />

              {/* Pricing */}
              <Route path={ROUTES.PRICING} element={<PricingPage />} />

              {/* Semi-Protected Routes (can be used without auth but better with) */}
              <Route path={ROUTES.CART} element={<CartPage />} />

              {/* Protected Routes */}
              <Route
                path={ROUTES.CHECKOUT}
                element={
                  <ProtectedRoute>
                    <CheckoutPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ORDER_HISTORY}
                element={
                  <ProtectedRoute>
                    <OrderHistoryPage />
                  </ProtectedRoute>
                }
              />

              {/* Photographer Dashboard Routes */}
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute requiredRole="photographer">
                    <DashboardApp />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="tenants" element={<TenantsList />} />
                <Route path="tenants/:tenantId" element={<TenantDetails />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="system-logs" element={<SystemLogsPage />} />
                <Route path="audit-logs" element={<AuditLogsPage />} />
                <Route path="showcase" element={<ShowcaseModeration />} />
              </Route>

              {/* Catch all route - redirect to home */}
              <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
              </Routes>
              </FavoritesProvider>
            </EventProvider>
          </CartProvider>
        </AuthProvider>
      </TenantProvider>
      </RecaptchaWrapper>
    </BrowserRouter>
  );
}

export default App;
